import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Loader2,
  Mail,
  Link,
  ToggleLeft,
  ToggleRight,
  Send,
  Copy,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const ClientPortalTab = ({ api }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await api().get('/client-portal/admin/clients');
      setClients(res.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const togglePortalAccess = async (clientId, currentEnabled) => {
    setProcessingId(clientId);
    try {
      if (currentEnabled) {
        await api().post(`/client-portal/admin/disable/${clientId}`);
        toast.success('Portal access disabled');
      } else {
        const res = await api().post(`/client-portal/admin/enable/${clientId}`);
        toast.success('Portal access enabled');
        
        // If new user was created, show invite option
        if (res.data.is_new_user) {
          toast.info('A new user account was created. Send them an invite to set their password.');
        }
      }
      fetchClients();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update portal access';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const sendInvite = async (clientId) => {
    setProcessingId(clientId);
    try {
      const res = await api().post(`/client-portal/admin/send-invite/${clientId}`);
      setInviteLink({
        clientName: res.data.client_name,
        email: res.data.email,
        link: res.data.invite_link
      });
      toast.success('Invite link generated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate invite';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Filter clients by search
  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Separate enabled and not enabled clients
  const enabledClients = filteredClients.filter(c => c.portal_enabled);
  const notEnabledClients = filteredClients.filter(c => !c.portal_enabled);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Client Portal Access
        </h2>
        <p className="text-zinc-500 text-sm mt-1">
          Manage which clients can access the client portal to view their bookings and communicate with your team.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients by name or email..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-lg text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          data-testid="client-search"
        />
      </div>

      {/* Invite Link Modal */}
      {inviteLink && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-green-800">Invite Link for {inviteLink.clientName}</h3>
              <p className="text-sm text-green-700 mt-1">
                Send this link to <strong>{inviteLink.email}</strong> so they can set their password:
              </p>
              <div className="mt-2 p-2 bg-white rounded border border-green-200 text-sm text-zinc-700 break-all">
                {inviteLink.link}
              </div>
            </div>
            <button
              onClick={() => setInviteLink(null)}
              className="text-green-600 hover:text-green-800"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => copyToClipboard(inviteLink.link)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-green-300 rounded-lg text-sm text-green-700 hover:bg-green-50"
            >
              <Copy className="h-4 w-4" />
              Copy Link
            </button>
            <a
              href={`mailto:${inviteLink.email}?subject=Your%20T%20Touch%20Client%20Portal%20Access&body=Hi%20${inviteLink.clientName},%0A%0AYou%20can%20now%20access%20your%20booking%20information%20through%20our%20client%20portal.%0A%0AClick%20this%20link%20to%20set%20your%20password:%0A${encodeURIComponent(inviteLink.link)}%0A%0ABest%20regards,%0AT%20Touch%20Cleaning%20Services`}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600 rounded-lg text-sm text-white hover:bg-green-700"
            >
              <Mail className="h-4 w-4" />
              Send Email
            </a>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{enabledClients.length}</div>
          <div className="text-sm text-zinc-500">Portal Enabled</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-zinc-400">{notEnabledClients.length}</div>
          <div className="text-sm text-zinc-500">Not Enabled</div>
        </div>
      </div>

      {/* Enabled Clients */}
      {enabledClients.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-zinc-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            Clients with Portal Access ({enabledClients.length})
          </h3>
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {enabledClients.map(client => (
              <ClientRow
                key={client.id}
                client={client}
                processing={processingId === client.id}
                onToggle={() => togglePortalAccess(client.id, true)}
                onSendInvite={() => sendInvite(client.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Not Enabled Clients */}
      {notEnabledClients.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-zinc-700 flex items-center gap-2">
            <XCircle className="h-4 w-4 text-zinc-400" />
            Clients without Portal Access ({notEnabledClients.length})
          </h3>
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {notEnabledClients.map(client => (
              <ClientRow
                key={client.id}
                client={client}
                processing={processingId === client.id}
                onToggle={() => togglePortalAccess(client.id, false)}
                onSendInvite={() => sendInvite(client.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredClients.length === 0 && (
        <div className="text-center py-12 bg-zinc-50 rounded-lg border border-zinc-200">
          <Users className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">
            {search ? 'No clients match your search' : 'No clients found'}
          </p>
          <p className="text-zinc-400 text-sm mt-1">
            Add clients in the Clients section to enable portal access
          </p>
        </div>
      )}

      {/* Portal URL Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-medium text-amber-800 flex items-center gap-2">
          <ExternalLink className="h-4 w-4" />
          Client Portal URL
        </h3>
        <p className="text-sm text-amber-700 mt-1">
          Clients can access the portal at:
        </p>
        <div className="mt-2 p-2 bg-white rounded border border-amber-200 text-sm text-zinc-700 flex items-center justify-between">
          <code>https://thettouch.app/portal/login</code>
          <button
            onClick={() => copyToClipboard('https://thettouch.app/portal/login')}
            className="text-amber-600 hover:text-amber-800"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

function ClientRow({ client, processing, onToggle, onSendInvite }) {
  const hasEmail = !!client.email;
  const isEnabled = client.portal_enabled;

  return (
    <div className="p-4 flex items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-zinc-900">{client.name}</div>
        {hasEmail ? (
          <div className="text-sm text-zinc-500 flex items-center gap-1">
            <Mail className="h-3.5 w-3.5" />
            {client.email}
          </div>
        ) : (
          <div className="text-sm text-red-500 flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5" />
            No email address
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Send Invite (only if enabled) */}
        {isEnabled && hasEmail && (
          <button
            onClick={onSendInvite}
            disabled={processing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
            data-testid={`send-invite-${client.id}`}
          >
            {processing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Invite
          </button>
        )}

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          disabled={processing || !hasEmail}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
            isEnabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
          }`}
          title={!hasEmail ? 'Client needs an email address' : ''}
          data-testid={`toggle-portal-${client.id}`}
        >
          {processing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isEnabled ? (
            <>
              <ToggleRight className="h-4 w-4" />
              Enabled
            </>
          ) : (
            <>
              <ToggleLeft className="h-4 w-4" />
              Enable
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ClientPortalTab;
